import { useState, useEffect } from 'react';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

export interface Course {
  id: string;
  name: string;
  location: string | null;
  city: string | null;
  country: string | null;
  description: string | null;
  par: number;
  holes_count: number;
  website: string | null;
  phone: string | null;
  email: string | null;
  logo_url: string | null;
  image_url: string | null;
  established_year: number | null;
  slope_rating: number | null;
  course_rating: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Props {
  course: Course | null;
  onClose: () => void;
  onSaved: (course: Course) => void;
}

const defaultForm = {
  name: '',
  location: '',
  city: '',
  country: '',
  description: '',
  par: 72,
  holes_count: 18,
  website: '',
  phone: '',
  email: '',
  logo_url: '',
  image_url: '',
  established_year: '',
  slope_rating: '',
  course_rating: '',
  is_active: true,
};

export default function CourseModal({ course, onClose, onSaved }: Props) {
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'basic' | 'ratings' | 'contact' | 'media'>('basic');

  useEffect(() => {
    if (course) {
      setForm({
        name: course.name,
        location: course.location ?? '',
        city: course.city ?? '',
        country: course.country ?? '',
        description: course.description ?? '',
        par: course.par,
        holes_count: course.holes_count,
        website: course.website ?? '',
        phone: course.phone ?? '',
        email: course.email ?? '',
        logo_url: course.logo_url ?? '',
        image_url: course.image_url ?? '',
        established_year: course.established_year != null ? String(course.established_year) : '',
        slope_rating: course.slope_rating != null ? String(course.slope_rating) : '',
        course_rating: course.course_rating != null ? String(course.course_rating) : '',
        is_active: course.is_active,
      });
    } else {
      setForm(defaultForm);
    }
  }, [course]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Course name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        description: form.description.trim() || null,
        par: Number(form.par),
        holes_count: Number(form.holes_count),
        website: form.website.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        logo_url: form.logo_url.trim() || null,
        image_url: form.image_url.trim() || null,
        established_year: form.established_year ? Number(form.established_year) : null,
        slope_rating: form.slope_rating ? Number(form.slope_rating) : null,
        course_rating: form.course_rating ? Number(form.course_rating) : null,
        is_active: form.is_active,
        updated_at: new Date().toISOString(),
      };

      if (course) {
        const { data, error } = await supabase
          .from('courses')
          .update(payload)
          .eq('id', course.id)
          .select()
          .single();
        if (error) throw error;
        toast.success('Course updated');
        onSaved(data as Course);
      } else {
        const { data, error } = await supabase
          .from('courses')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        toast.success('Course created');
        onSaved(data as Course);
      }
    } catch (err: unknown) {
      console.error(err);
      const msg = (err as { message?: string })?.message ?? 'Failed to save course';
      toast.error(msg.includes('relation') || msg.includes('does not exist')
        ? 'Database table not found. Please run the migration first.'
        : msg);
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { key: 'basic', label: 'Basic Info' },
    { key: 'ratings', label: 'Ratings & Layout' },
    { key: 'contact', label: 'Contact' },
    { key: 'media', label: 'Media' },
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-golf-gray">
              {course ? 'Edit Course' : 'Add Course'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {course ? `Editing ${course.name}` : 'Create a new golf course'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex border-b border-gray-100 px-6">
          {sections.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setActiveSection(s.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeSection === s.key
                  ? 'border-golf-orange text-golf-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {activeSection === 'basic' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="e.g. Augusta National Golf Club"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                      placeholder="e.g. Augusta"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                      placeholder="e.g. United States"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location / Address</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="Full address or general location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm resize-none"
                    placeholder="Brief description of the course..."
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-gray-200 rounded-full peer peer-checked:bg-golf-green peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                  </label>
                  <span className="text-sm font-medium text-gray-700">Active Course</span>
                </div>
              </>
            )}

            {activeSection === 'ratings' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Par</label>
                    <input
                      type="number"
                      value={form.par}
                      onChange={(e) => setForm({ ...form, par: Number(e.target.value) })}
                      min={54}
                      max={90}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Holes</label>
                    <select
                      value={form.holes_count}
                      onChange={(e) => setForm({ ...form, holes_count: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm bg-white"
                    >
                      <option value={9}>9 holes</option>
                      <option value={18}>18 holes</option>
                      <option value={27}>27 holes</option>
                      <option value={36}>36 holes</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Course Rating
                      <span className="text-gray-400 font-normal ml-1">(USGA)</span>
                    </label>
                    <input
                      type="number"
                      value={form.course_rating}
                      onChange={(e) => setForm({ ...form, course_rating: e.target.value })}
                      step="0.1"
                      min="55"
                      max="80"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                      placeholder="e.g. 72.4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slope Rating
                    </label>
                    <input
                      type="number"
                      value={form.slope_rating}
                      onChange={(e) => setForm({ ...form, slope_rating: e.target.value })}
                      step="0.5"
                      min="55"
                      max="155"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                      placeholder="e.g. 113.0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Established Year</label>
                  <input
                    type="number"
                    value={form.established_year}
                    onChange={(e) => setForm({ ...form, established_year: e.target.value })}
                    min={1800}
                    max={new Date().getFullYear()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="e.g. 1933"
                  />
                </div>

                <div className="bg-golf-sand/30 rounded-xl p-4">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    <strong>Course Rating</strong> is the USGA evaluation of the difficulty of a course for a scratch golfer (e.g., 72.4).
                    <br />
                    <strong>Slope Rating</strong> represents the relative difficulty for a bogey golfer compared to a scratch golfer. Standard is 113.
                  </p>
                </div>
              </>
            )}

            {activeSection === 'contact' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="https://www.example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="info@course.com"
                  />
                </div>
              </>
            )}

            {activeSection === 'media' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course Logo URL
                  </label>
                  <input
                    type="url"
                    value={form.logo_url}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="https://..."
                  />
                  {form.logo_url && (
                    <div className="mt-2 w-16 h-16 rounded-lg border border-gray-200 overflow-hidden">
                      <img src={form.logo_url} alt="Logo preview" className="w-full h-full object-contain p-1" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course Image URL
                    <span className="text-gray-400 font-normal ml-1">(header/banner)</span>
                  </label>
                  <input
                    type="url"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
                    placeholder="https://..."
                  />
                  {form.image_url ? (
                    <div className="mt-2 w-full h-32 rounded-lg border border-gray-200 overflow-hidden">
                      <img src={form.image_url} alt="Image preview" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  ) : (
                    <div className="mt-2 w-full h-24 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center">
                      <div className="text-center">
                        <PhotoIcon className="w-6 h-6 text-gray-300 mx-auto" />
                        <p className="text-xs text-gray-400 mt-1">No image set</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-golf-orange hover:bg-golf-orange/90 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : course ? 'Save Changes' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
